import type { PosTerminal, PosTerminalCreate, PosTerminalUpdate, Warehouse } from '@erp/shared';
import { Ban, Monitor, Pencil, RotateCcw, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Badge,
  Button,
  Card,
  Combobox,
  confirmDialog,
  CopyableCode,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Skeleton,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { cn } from '../../lib/cn.js';
import { listUsers, type UserListItem } from '../../lib/users.js';

const EMPTY_FORM: Omit<PosTerminalCreate, 'defaultCurrencyCode'> = {
  code: '',
  name: '',
  location: undefined,
};

export function PosTerminalsPage(): JSX.Element {
  const { t } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const [items, setItems] = useState<PosTerminal[] | null>(null);
  // The pairing code, shown once. It is not recoverable — the server keeps only
  // a hash — so this dialog is the single moment it can be read out.
  const [pairing, setPairing] = useState<{ name: string; code: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PosTerminal | null>(null);
  // For the warehouse column + the form's picker. Each till can be pinned to
  // the house it sells out of; unpinned tills use the tenant default.
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: PosTerminal[] }>('/pos/terminals');
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload();
    void api()
      .get<{ items: Warehouse[]; defaultWarehouseId?: string | null }>('/warehouses')
      .then((r) => {
        setWarehouses(r.data.items.filter((w) => w.isActive));
        setDefaultWarehouseId(r.data.defaultWarehouseId ?? null);
      })
      .catch(() => { /* non-fatal — column shows the default label */ });
  }, []);

  const openEdit = (term: PosTerminal): void => {
    setCreating(false);
    setEditing(term);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeactivate = async (term: PosTerminal): Promise<void> => {
    if (!(await confirmDialog({ message: t('terminals.actions.confirmDeactivate', { code: term.code, name: term.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/pos/terminals/${term.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (term: PosTerminal): Promise<void> => {
    if (!(await confirmDialog({ message: t('terminals.actions.confirmReactivate', { code: term.code, name: term.name }) }))) return;
    try {
      // PosTerminalUpdateSchema accepts isActive; mirror DELETE.
      await api().patch(`/pos/terminals/${term.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  if (items === null) return <Card><Skeleton className="h-32 w-full" /></Card>;

  /**
   * Mint a pairing code for this counter. The cashier signs into the till
   * normally and types it in once; from then on the till proves which terminal
   * it is at every login, which is what makes "this account is for the till"
   * a real restriction rather than a request the client can decline.
   */
  const onPair = async (term: PosTerminal): Promise<void> => {
    try {
      const r = await api().post<{ code: string }>(`/pos/terminals/${term.id}/pairing-code`);
      setPairing({ name: term.name, code: r.data.code });
    } catch {
      toast.error(tc('errors:server', { defaultValue: 'Something went wrong.' }));
    }
  };

  const columns: DataTableColumn<PosTerminal>[] = [
    {
      id: 'code',
      header: t('terminals.table.code'),
      sortValue: (tt) => tt.code,
      searchValue: (tt) => tt.code,
      cellClassName: 'font-mono text-xs',
      cell: (tt) => tt.code,
    },
    {
      id: 'name',
      header: t('terminals.table.name'),
      sortValue: (tt) => tt.name,
      searchValue: (tt) => tt.name,
      cell: (tt) => tt.name,
    },
    {
      id: 'location',
      header: t('terminals.table.location'),
      sortValue: (tt) => tt.location ?? '',
      searchValue: (tt) => tt.location ?? '',
      cellClassName: 'text-fg-muted',
      cell: (tt) => tt.location ?? '—',
    },
    {
      id: 'currency',
      header: t('terminals.table.currency'),
      sortValue: (tt) => tt.defaultCurrencyCode,
      searchValue: (tt) => tt.defaultCurrencyCode,
      cell: (tt) => tt.defaultCurrencyCode,
    },
    {
      id: 'warehouse',
      header: t('terminals.table.warehouse', { defaultValue: 'Warehouse' }),
      sortValue: (tt) => (tt.warehouseId ? warehouses.find((w) => w.id === tt.warehouseId)?.name ?? '' : ''),
      cell: (tt) => {
        const wh = tt.warehouseId ? warehouses.find((w) => w.id === tt.warehouseId) : undefined;
        if (wh) return <span className="text-xs font-medium">{wh.name}</span>;
        const def = defaultWarehouseId ? warehouses.find((w) => w.id === defaultWarehouseId) : undefined;
        return (
          <span className="text-xs text-fg-muted">
            {t('terminals.table.defaultWarehouse', { defaultValue: 'Default' })}
            {def ? ` (${def.name})` : ''}
          </span>
        );
      },
    },
    {
      id: 'active',
      header: t('terminals.table.active'),
      sortValue: (tt) => (tt.isActive ? 1 : 0),
      cell: (tt) => (
        <Badge tone={tt.isActive ? 'success' : 'neutral'}>
          {t(`terminals.status.${tt.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-32',
      cell: (tt) => (
        <div className="flex justify-end gap-1">
          {/* Pair a desktop till to this counter. Until it is paired the .exe
              has no identity, so a role restricted to the till cannot tell it
              from a browser — and an unpaired till keeps selling meanwhile. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void onPair(tt)}
            aria-label={`${t('terminals.actions.pair', { defaultValue: 'Pair a till' })} ${tt.code}`}
            title={t('terminals.actions.pair', { defaultValue: 'Pair a till' })}
          >
            <Smartphone className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEdit(tt)}
            aria-label={`${t('terminals.actions.edit')} ${tt.code}`}
            title={t('terminals.actions.edit')}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          {tt.isActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeactivate(tt)}
              aria-label={`${t('terminals.actions.deactivate')} ${tt.code}`}
              title={t('terminals.actions.deactivate')}
            >
              <Ban className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReactivate(tt)}
              aria-label={`${t('terminals.actions.reactivate')} ${tt.code}`}
              title={t('terminals.actions.reactivate')}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('terminals.title')}</h1>
          <p className="text-sm text-fg-muted">{t('terminals.subtitle')}</p>
        </div>
        {!creating && !editing && (
          <Button onClick={() => setCreating(true)}>{t('terminals.newButton')}</Button>
        )}
      </div>

      {creating && (
        <TerminalForm
          mode="create"
          warehouses={warehouses}
          onCancel={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <TerminalForm
          mode="edit"
          initial={editing}
          warehouses={warehouses}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      {items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Monitor className="h-8 w-8" aria-hidden />}
            title={t('terminals.empty')}
            description={t('terminals.emptyDescription')}
            action={
              !creating && !editing ? (
                <Button onClick={() => setCreating(true)}>{t('terminals.newButton')}</Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(tt) => tt.id}
            searchPlaceholder={t('terminals.table.code')}
            rowClassName={(tt) => (tt.isActive ? undefined : 'opacity-60')}
          />
        </Card>
      )}
    
      {pairing && (
        <Dialog open onOpenChange={(o: boolean) => !o && setPairing(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('terminals.pair.title', { defaultValue: 'Pair a till' })}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-2">
              <p className="text-sm text-fg-muted">
                {t('terminals.pair.hint', {
                  name: pairing.name,
                  defaultValue: 'On the till, sign in as usual and enter this code once. It works for 15 minutes and cannot be shown again.',
                })}
              </p>
              <CopyableCode value={pairing.code} />
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setPairing(null)}>
                {tc('actions.done', { defaultValue: 'Done' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
</div>
  );
}

interface TerminalFormProps {
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'; populates the form and supplies the id for PATCH. */
  initial?: PosTerminal;
  warehouses: Warehouse[];
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function TerminalForm({ mode, initial, warehouses, onCancel, onSaved }: TerminalFormProps): JSX.Element {
  const { t } = useTranslation('pos');
  const isEdit = mode === 'edit';
  // New terminals default to the tenant's base currency (e.g. IQD) — never a
  // hardcoded USD, which silently put POS shifts in the wrong currency.
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const [form, setForm] = useState<PosTerminalCreate>(() =>
    initial
      ? {
          code: initial.code,
          name: initial.name,
          location: initial.location ?? undefined,
          defaultCurrencyCode: initial.defaultCurrencyCode,
          warehouseId: initial.warehouseId ?? undefined,
          // The extras only — the default is shown by its own picker above, and
          // sending it twice would just make it first in its own list again.
          warehouseIds: initial.warehouseIds.filter((w) => w !== initial.warehouseId),
          allWarehouses: initial.allWarehouses,
          cashierUserIds: initial.cashierUserIds,
        }
      : { ...EMPTY_FORM, defaultCurrencyCode: baseCurrency },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only users who can actually work a register are offerable as its cashiers.
  const [cashiers, setCashiers] = useState<UserListItem[]>([]);
  useEffect(() => {
    void listUsers('pos.sell').then(setCashiers).catch(() => setCashiers([]));
  }, []);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isEdit && initial) {
        // Code is the unique identifier — PosTerminalUpdateSchema accepts
        // it (Create.partial()) but we keep it read-only here. Renaming
        // would invalidate any audit-log references already on file.
        // Empty location collapses to null so the column can be cleared.
        const patch: PosTerminalUpdate = {
          name: form.name.trim(),
          location: form.location && form.location.trim() ? form.location.trim() : null,
          defaultCurrencyCode: form.defaultCurrencyCode.toUpperCase(),
          // null clears the pin — the till goes back to the tenant default.
          warehouseId: form.warehouseId ?? null,
          warehouseIds: form.warehouseIds ?? [],
          allWarehouses: form.allWarehouses ?? false,
          cashierUserIds: form.cashierUserIds ?? [],
        };
        await api().patch(`/pos/terminals/${initial.id}`, patch);
      } else {
        await api().post('/pos/terminals', {
          code: form.code.trim(),
          name: form.name.trim(),
          location: form.location || undefined,
          defaultCurrencyCode: form.defaultCurrencyCode.toUpperCase(),
          ...(form.warehouseId ? { warehouseId: form.warehouseId } : {}),
          warehouseIds: form.warehouseIds ?? [],
          allWarehouses: form.allWarehouses ?? false,
          cashierUserIds: form.cashierUserIds ?? [],
        });
      }
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'unprocessable') setError(t('terminals.errors.unprocessable'));
      else setError(code ?? String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const heading = isEdit && initial
    ? t('terminals.edit.title', { code: initial.code, name: initial.name })
    : t('terminals.newButton');

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{heading}</h2>
      {error && (
        <div
          className="mb-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </div>
      )}
      <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="t-code" label={t('terminals.table.code')} required>
          <Input
            id="t-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            autoFocus={!isEdit}
            readOnly={isEdit}
            aria-readonly={isEdit}
            className={isEdit ? 'cursor-not-allowed bg-bg-muted text-fg-muted' : undefined}
          />
        </FormField>
        <FormField id="t-name" label={t('terminals.table.name')} required>
          <Input
            id="t-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus={isEdit}
          />
        </FormField>
        <FormField id="t-loc" label={t('terminals.table.location')}>
          <Input
            id="t-loc"
            value={form.location ?? ''}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </FormField>
        <FormField id="t-cur" label={t('terminals.table.currency')}>
          <CurrencySelect
            id="t-cur"
            value={form.defaultCurrencyCode}
            onChange={(c) => setForm({ ...form, defaultCurrencyCode: c })}
          />
        </FormField>
        <FormField
          id="t-wh"
          label={t('terminals.table.warehouse', { defaultValue: 'Warehouse' })}
          helperText={t('terminals.warehouseHint', { defaultValue: 'Sales on this till are deducted from here. Empty = the default warehouse (Settings → Inventory).' })}
        >
          <Combobox
            id="t-wh"
            options={warehouses.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
            value={form.warehouseId ?? undefined}
            onChange={(v) => setForm({ ...form, warehouseId: v ?? undefined })}
            placeholder={t('terminals.table.defaultWarehouse', { defaultValue: 'Default' })}
            ariaLabel={t('terminals.table.warehouse', { defaultValue: 'Warehouse' })}
          />
        </FormField>
        <FormField
          id="t-wh-all"
          label={t('terminals.moreWarehouses', { defaultValue: 'Also sells from' })}
          helperText={t('terminals.moreWarehousesHint', { defaultValue: 'The cashier can switch between these on the register and on the .exe till. The warehouse above stays the default.' })}
        >
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allWarehouses ?? false}
                onChange={(e) => setForm({ ...form, allWarehouses: e.target.checked })}
              />
              <span>{t('terminals.allWarehouses', { defaultValue: 'Every warehouse (including ones added later)' })}</span>
            </label>
            {!form.allWarehouses && (
              <div className="flex flex-wrap gap-1.5">
                {warehouses.filter((w) => w.id !== form.warehouseId).map((w) => {
                  const on = (form.warehouseIds ?? []).includes(w.id);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        warehouseIds: on
                          ? (form.warehouseIds ?? []).filter((x) => x !== w.id)
                          : [...(form.warehouseIds ?? []), w.id],
                      })}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs transition',
                        on ? 'border-brand bg-brand/10 text-fg' : 'border-border text-fg-muted hover:border-brand/50',
                      )}
                    >
                      {w.code} — {w.name}
                    </button>
                  );
                })}
                {warehouses.filter((w) => w.id !== form.warehouseId).length === 0 && (
                  <span className="text-xs text-fg-muted">{t('terminals.noOtherWarehouses', { defaultValue: 'No other warehouses yet.' })}</span>
                )}
              </div>
            )}
          </div>
        </FormField>
        <FormField
          id="t-cashiers"
          label={t('terminals.cashiers', { defaultValue: 'Cashiers' })}
          helperText={t('terminals.cashiersHint', { defaultValue: 'Only these may open this till. None selected = anyone who can use the register.' })}
        >
          <div className="flex flex-wrap gap-1.5">
            {cashiers.map((u) => {
              const on = (form.cashierUserIds ?? []).includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setForm({
                    ...form,
                    cashierUserIds: on
                      ? (form.cashierUserIds ?? []).filter((x) => x !== u.id)
                      : [...(form.cashierUserIds ?? []), u.id],
                  })}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition',
                    on ? 'border-brand bg-brand/10 text-fg' : 'border-border text-fg-muted hover:border-brand/50',
                  )}
                >
                  {u.fullName}
                </button>
              );
            })}
            {cashiers.length === 0 && (
              <span className="text-xs text-fg-muted">{t('terminals.noCashiers', { defaultValue: 'No users with register access yet.' })}</span>
            )}
          </div>
        </FormField>
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={busy}>
            {isEdit ? t('terminals.edit.submit') : t('terminals.create.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            {isEdit ? t('terminals.edit.cancel') : t('terminals.create.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
